import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    Output,
    ViewChild,
} from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
    MatAutocomplete,
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent,
    MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { filter, isEqual } from 'lodash-es';
import { BaseFieldComponent } from 'vcp-common-ui-lib';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';

/** Example of chips autocomplete usage
 * HTML
 * <vc-chips-autocomplete label="Chips Autocomplete"
 *     [(values)]="selectedChips"
 *     [required]="true"
 *     [options]="chipsOptions"
 *     [itemRenderer]="chipsAutocompleteItemRenderer"></vc-chips-autocomplete>
 *
 * TS
 *  chipsOptions: { position: number; name: string }[] = [
 *    { position: 1, name: 'Hydrogen' },
 *    { position: 2, name: 'Helium' },
 *     { position: 3, name: 'Lithium' },
 *  ];
 *
 *  selectedChips: { position: number; name: string }[] = [this.chipsOptions[0]];
 *
 *  public chipsAutocompleteItemRenderer = (value: { position: number; name: string }) => value?.name ?? '';
 *
 * */

@Component({
    selector: 'vc-chips-input-autocomplete',
    templateUrl: './chips-input-autocomplete.component.html',
    styleUrls: ['./chips-input-autocomplete.component.scss'],
    imports: [MatFormFieldModule, MatChipsModule, MatIconModule, MatAutocompleteModule, FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipsInputAutocompleteComponent<T> extends BaseFieldComponent<T> {
    private _values: T[] = [];
    private _options: T[] = [];
    private _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    filteredOptions!: T[];
    separatorKeysCodes: number[] = [ENTER, COMMA];
    filterValue: string = '';

    @Input()
    set values(val: T[]) {
        if (val != this._values) {
            this._values = val;
            this._setAutocompleteItems();
            this.valuesChange.emit(this._values);
            this.validate();
        }
    }

    get values(): T[] {
        return this._values;
    }

    /** List of options for chips autocomplete */
    @Input()
    set options(value: T[]) {
        this._options = value;
        this.filteredOptions = this._options;
        this._setAutocompleteItems();
    }

    get options(): T[] {
        return this._options;
    }

    /** Autocomplete placeholder value */
    @Input()
    placeholder!: string;

    /** Identifies the element (or elements) that describes the autocomplete. */
    @Input()
    ariaDescribedBy!: string;

    /** Function for rendering options list and selected values */
    @Input()
    itemRenderer: (data: T) => string = (value: T) => `${value}`;

    /** Two ways data binding for autocomplete value */
    @Output()
    valuesChange = new EventEmitter<T[]>();

    @ViewChild('autocompleteInput')
    autocompleteInput!: ElementRef<HTMLInputElement>;

    @ViewChild(MatAutocompleteTrigger)
    autocomplete!: MatAutocompleteTrigger;

    @ViewChild('auto')
    matAutocomplete!: MatAutocomplete;

    add(event: MatChipInputEvent): void {
        const input = event.chipInput;
        const value = (event.value || '').trim();
        if ((value || '').trim()) {
            if (!this._values.includes(value.toString().trim() as T)) {
                this.values.push(value.toString().trim() as T);
            }
        }
        // Reset the input value
        if (input) {
            this.autocompleteInput.nativeElement.value = '';
        }
    }

    remove(chipData: T): void {
        this.values = filter(this.values, (value) => !isEqual(chipData, value));
        this.filteredOptions = [...this.filteredOptions, chipData];
        this.autocomplete.closePanel();
    }

    selectValue(event: MatAutocompleteSelectedEvent): void {
        this.values = [...(this.values ?? []), event.option.value];
        this.autocompleteInput.nativeElement.value = '';
    }

    inputValueChanges() {
        // Filter items only when user typing
        if (typeof this.filterValue === 'string' || typeof this.filterValue === 'number') {
            this.filteredOptions = this._filter(this.filterValue);
        }
    }

    /** Validation function */
    validate(): boolean {
        this.valid = true;
        if (this.required && this._values.length == 0) {
            this.errorMessage = `${this.label ?? this.fieldText} ${this.requiredText}`;
            this.valid = false;
        }

        if (this.validationFunction != null && this.valid) {
            this.errorMessage = this.validationFunction(this._values);
            this.valid = this.errorMessage == '';
        }

        return this.valid;
    }

    clear() {
        if (!this.readonly && !this.disabled) {
            this.values = [];
        }
        this.filterValue = '';
        this.filteredOptions = this.options;
        this.valid = true;
        this.touched = false;
        this._changeDetectorRef.markForCheck();
    }

    private _setAutocompleteItems() {
        this.filteredOptions = filter(this.filteredOptions, (value) => !this._values.includes(value));
    }

    private _filter(value: string | number): T[] {
        const filterValue = value?.toString()?.toLowerCase();
        return this.options?.filter(
            (option: T) =>
                (this.itemRenderer(option) ?? '').toString()?.toLowerCase()?.includes(filterValue) &&
                !this._values.includes(option)
        );
    }
}
